'use client';

import { ErrorBoundaryMeta } from '@liveauctioneers/hammer-ui-core/providers';
import { LlamaErrorScreen } from '@/components/ErrorScreens/LlamaErrorScreen';

export type DefaultErrorFallbackProps = {
    error: Error;
    meta?: ErrorBoundaryMeta;
    resetError: () => void;
};

/**
 * Default error boundary fallback component using the unified LlamaErrorScreen.
 *
 * All ErrorBoundaries show the same recognizable llama error screen,
 * making it easy for users and support to identify when something goes wrong.
 *
 * This component is extracted for testability and can be used directly in tests.
 */
export const DefaultErrorFallback = ({ error, resetError }: DefaultErrorFallbackProps) => {
    return (
        <LlamaErrorScreen
            error={error}
            onGoHome={() => (window.location.href = '/')}
            onReset={resetError}
            showDetails={process.env.NODE_ENV === 'development'}
        />
    );
};

/**
 * Adapter function that matches the HammerUIProvider defaultFallback signature.
 * Use this when passing to HammerUIProvider.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- meta is available but not needed for unified LlamaErrorScreen
export const defaultErrorFallbackAdapter = (error: Error, resetError: () => void, _meta?: ErrorBoundaryMeta) => {
    return (
        <DefaultErrorFallback
            error={error}
            resetError={resetError}
        />
    );
};
